// v1.21.0 · Portada inmersiva: al bajar, una nube de datos sueltos se ordena en la forma de cada servicio
//           (percha · torre · equipo · app · reloj de 48 h). El texto de cada servicio acompaña a la escultura.
//           Con «reducir movimiento» la escultura no gira ni se transforma: cambia de forma en cada capítulo.
import React, { useEffect, useRef, useState } from 'react';
import { CalendarCheck2, ArrowDown, Pause, Play } from 'lucide-react';
import { createSculpture } from './sculpt';
import { services, BOOKING_URL } from '../../data/site';

// «en 48 horas» va con resaltador: es la promesa principal de la portada
const MARK_RX = /(en 48 horas)/i;
function hl(text) {
  if (!text) return text;
  return text.split(MARK_RX).map((part, k) => (k % 2 ? <mark key={k} className="imm-mark">{part}</mark> : part));
}

// Recorrido automático: si nadie toca la página, la portada avanza sola por los 6 capítulos (~30 s).
// Cualquier gesto del visitante (rueda, toque, tecla, clic) lo detiene; el botón Pausar / Reproducir lo controla.
const AUTO_HOLD_MS = 7600;      // tiempo quieto en cada capítulo (+ 2,4 s de paso ≈ 10 s por capítulo)
const AUTO_MOVE_MS = 2400;      // duración del paso al siguiente (scroll suave con aceleración y frenado)
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

export default function SiteImmersive() {
  const wrap = useRef(null);
  const canvas = useRef(null);
  const sculpt = useRef(null);
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(false);
  const autoRef = useRef(false);
  const chapters = services.length;   // portada + servicios + demo

  const chapterTop = (i) => {
    const section = wrap.current;
    const span = section.offsetHeight - window.innerHeight;
    return section.offsetTop + (span * i) / (chapters - 1) + 2;
  };
  const currentChapter = () => {
    const section = wrap.current;
    const span = Math.max(1, section.offsetHeight - window.innerHeight);
    return Math.round(Math.max(0, Math.min(1, (window.scrollY - section.offsetTop) / span)) * (chapters - 1));
  };

  // Arranca solo si la página se abrió arriba y el visitante no pidió menos movimiento
  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!reduced && window.scrollY < 10) setAuto(true);
  }, []);

  useEffect(() => {
    autoRef.current = auto;
    if (!auto || !wrap.current) return undefined;
    let raf = 0; let timer = 0; let alive = true;
    const glide = (to, done) => {
      const from = window.scrollY; const t0 = performance.now();
      const step = (now) => {
        if (!alive) return;
        const k = Math.min(1, (now - t0) / AUTO_MOVE_MS);
        window.scrollTo(0, from + (to - from) * easeInOut(k));
        if (k < 1) raf = requestAnimationFrame(step); else done();
      };
      raf = requestAnimationFrame(step);
    };
    const next = () => {
      if (!alive) return;
      if (document.hidden) { timer = window.setTimeout(next, 1000); return; }
      const i = currentChapter();
      if (i >= chapters - 1) { setAuto(false); return; }   // llegó a «Demo en 48 h»: termina el recorrido
      glide(chapterTop(i + 1), () => { timer = window.setTimeout(next, AUTO_HOLD_MS); });
    };
    timer = window.setTimeout(next, AUTO_HOLD_MS);
    const stop = (e) => { if (!e.target?.closest?.('.imm-auto')) setAuto(false); };
    const onKey = (e) => { if (!e.target?.closest?.('.imm-auto')) setAuto(false); };
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('pointerdown', stop);
    window.addEventListener('keydown', onKey);
    return () => {
      alive = false; cancelAnimationFrame(raf); window.clearTimeout(timer);
      window.removeEventListener('wheel', stop); window.removeEventListener('touchstart', stop);
      window.removeEventListener('pointerdown', stop); window.removeEventListener('keydown', onKey);
    };
  }, [auto]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = canvas.current;
    const section = wrap.current;
    if (!el || !section) return undefined;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const s = createSculpture(el, { mobile: window.innerWidth < 820, reduced });
    sculpt.current = s;
    const onScroll = () => {
      const r = section.getBoundingClientRect();
      const span = Math.max(1, section.offsetHeight - window.innerHeight);
      const p = Math.max(0, Math.min(1, -r.top / span)) * (chapters - 1);
      s.setProgress(p);
      setActive(Math.round(p));
    };
    const onMove = (e) => s.setPointer(e.clientX / window.innerWidth - 0.5, e.clientY / window.innerHeight - 0.5);
    const onResize = () => { s.resize(); onScroll(); };
    // Solo anima mientras la sección está en pantalla
    const io = new IntersectionObserver(([en]) => (en.isIntersecting ? s.start() : s.stop()), { threshold: 0 });
    io.observe(section);
    onScroll();
    s.draw();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      io.disconnect(); s.stop();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', onMove);
    };
  }, [chapters]);

  const goTo = (i) => {
    if (!wrap.current) return;
    setAuto(false);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: chapterTop(i), behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <section id="inicio" ref={wrap} className="imm" style={{ height: `${chapters * 100}svh` }} data-testid="site-immersive"
             aria-label="Qué construimos">
      {/* Ancla del menú «Servicios»: el primer capítulo después de la portada */}
      <div id="servicios" className="imm-anchor" style={{ top: '100svh' }} aria-hidden />
      <div className="imm-stage">
        <canvas ref={canvas} className="imm-canvas" aria-hidden />
        <div className="imm-shade" aria-hidden />

        <div className="imm-copy">
          {services.map((c, i) => (
            <article key={c.key} className="imm-chapter" data-on={i === active ? '' : undefined} aria-hidden={i !== active}
                     data-testid={`imm-${c.key}`}>
              {c.kind === 'intro' ? (
                <>
                  <h1 className="imm-title">{hl(c.title)}</h1>
                  <p className="imm-lead">{hl(c.lead)}</p>
                  <div className="imm-ctas">
                    <a href={BOOKING_URL} className="imm-btn" tabIndex={i === active ? 0 : -1}>
                      <CalendarCheck2 size={18} aria-hidden /> Agendar una llamada de 30 min
                    </a>
                    <button type="button" onClick={() => goTo(1)} className="imm-link" tabIndex={i === active ? 0 : -1}>
                      Ver qué construimos <ArrowDown size={16} aria-hidden />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="imm-name">{hl(c.name)}</h2>
                  <p className="imm-lead">{c.kind === 'demo' ? c.lead : hl(c.lead)}</p>
                  {c.items && (
                    <ul className="imm-items">
                      {c.items.map((it) => <li key={it}>{it}</li>)}
                    </ul>
                  )}
                  {c.kind === 'demo' ? (
                    <a href={BOOKING_URL} className="imm-btn" tabIndex={i === active ? 0 : -1}>
                      <CalendarCheck2 size={18} aria-hidden /> Reservar mi demo
                    </a>
                  ) : c.proof ? <p className="imm-proof">{c.proof}</p> : null}
                </>
              )}
            </article>
          ))}
        </div>

        <button type="button" className="imm-auto" onClick={() => setAuto((v) => !v)} aria-pressed={!auto}
                data-testid="imm-auto">
          {auto ? <Pause size={14} aria-hidden /> : <Play size={14} aria-hidden />}
          {auto ? 'Pausar recorrido' : 'Ver recorrido'}
        </button>

        <nav className="imm-index" aria-label="Servicios">
          {services.map((c, i) => (i === 0 ? null : (
            <button key={c.key} type="button" onClick={() => goTo(i)} aria-current={i === active ? 'step' : undefined}
                    className="imm-index-item">
              <span className="imm-index-bar" aria-hidden />{c.short}
            </button>
          )))}
        </nav>
      </div>
    </section>
  );
}
